/**
 * The cohort table: «درصد موفقیت» by month of first contact.
 *
 * THE GREY CELLS ARE THE POINT. A cohort met last week has not had time to
 * subscribe, so a recent row always looks terrible — and drawing that as a low
 * number invents a decline that is an artefact of the calendar rather than a
 * fact about the business. An immature cell is empty and grey; a cell that
 * genuinely converted nobody is a `۰`.
 *
 * That distinction survives the whole way from the analytics service, which
 * sends a null mask beside its values because a proto3 double cannot be null.
 */
import React from 'react';
import { formatCount, toPersianDigits } from '../../lib/format.js';
import { S } from '../../lib/strings.js';

function cellColour(value) {
  if (value === null || value === undefined) return 'bg-surface-hover text-transparent';
  if (value >= 50) return 'bg-primary text-primary-contrast';
  if (value >= 25) return 'bg-primary/60 text-text';
  if (value > 0) return 'bg-primary/25 text-text';
  return 'bg-surface text-text-muted';
}

export function CohortTable({ metric }) {
  if (!metric?.rows?.length) {
    return <p className="p-4 text-sm text-text-muted">{S.dashboard.noData}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="text-text-muted">
            <th className="sticky start-0 bg-surface p-2 text-start font-medium">
              {S.dashboard.cohortMonth}
            </th>
            <th className="p-2 text-start font-medium">{S.dashboard.cohortSize}</th>
            {metric.buckets.map((b) => (
              <th key={b} className="p-2 text-center font-medium">
                {toPersianDigits(b)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metric.rows.map((row) => (
            <tr key={row.key} className="border-t border-border">
              <th
                scope="row"
                className="sticky start-0 whitespace-nowrap bg-surface p-2 text-start font-normal"
              >
                {row.label}
              </th>
              <td className="p-2 tabular-nums text-text-muted">
                {formatCount(JSON.parse(row.extraJson ?? '{}').denominator ?? 0)}
              </td>
              {row.values.map((value, i) => (
                <td
                  key={i}
                  className={`p-2 text-center tabular-nums ${cellColour(value)}`}
                  title={
                    value === null || value === undefined
                      ? S.dashboard.immature
                      : `${toPersianDigits(value.toFixed(1))}٪`
                  }
                >
                  {value === null || value === undefined
                    ? '·'
                    : toPersianDigits(Math.round(value))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="p-2 text-xs text-text-muted">{S.dashboard.immature}: ·</p>
    </div>
  );
}
